/**
 * EditModeBar — calm entry affordance for edit mode (core-flows §3).
 *
 * Done button restyled as iOS-style pill in edit mode (§2.4, D-016).
 * Entry/exit animation classes applied for slide-up/down motion (§2.4 / SHOULD #15).
 * Immutability: functional component — no mutation of props or state.
 */
import React, { useEffect, useState } from 'react';

interface EditModeBarProps {
  readonly editMode: boolean;
  readonly onToggle: () => void;
  readonly onAddTile: () => void;
}

export function EditModeBar({ editMode, onToggle, onAddTile }: EditModeBarProps): React.ReactElement {
  // Track entry animation for Done pill (§2.4 / SHOULD #15).
  const [pillClass, setPillClass] = useState<'done-pill--entering' | ''>('');

  useEffect(() => {
    if (editMode) {
      setPillClass('done-pill--entering');
      // Remove class after animation duration (180ms + buffer).
      const t = setTimeout(() => setPillClass(''), 250);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [editMode]);

  if (!editMode) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          padding: 'var(--space-3)',
        }}
      >
        <button
          type="button"
          aria-label="Edit your board"
          onClick={onToggle}
          style={{
            background: 'none',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-button)',
            color: 'var(--color-text-dim)',
            fontSize: 'var(--text-detail)',
            fontWeight: 400,
            padding: '6px 16px',
            cursor: 'pointer',
            letterSpacing: '0.01em',
          }}
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div
      role="toolbar"
      aria-label="Edit mode controls"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-3) var(--space-4)',
        borderTop: '1px solid var(--color-border)',
        backgroundColor: 'var(--color-bg)',
        position: 'sticky',
        bottom: 0,
        zIndex: 100,
      }}
    >
      <span
        style={{
          fontSize: 'var(--text-label)',
          color: 'var(--color-text-dim)',
          fontWeight: 400,
        }}
      >
        Drag tiles to move · tap − to remove
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <button
          type="button"
          aria-label="Add a tile"
          onClick={onAddTile}
          style={{
            background: 'var(--color-accent)',
            border: 'none',
            borderRadius: 'var(--radius-button)',
            color: '#fff',
            fontSize: 'var(--text-label)',
            fontWeight: 500,
            padding: '6px 14px',
            cursor: 'pointer',
            minHeight: 36,
          }}
        >
          + Add tile
        </button>
        {/* Done pill (§2.4): full pill, accent fill, 80px min width. */}
        <button
          type="button"
          aria-label="Done editing"
          onClick={onToggle}
          className={pillClass}
          style={{
            background: 'var(--color-accent)',
            border: 'none',
            borderRadius: '18px',
            color: '#ffffff',
            fontSize: 'var(--text-label)',
            fontWeight: 600,
            padding: '0 var(--space-4)',
            minWidth: 80,
            height: 36,
            cursor: 'pointer',
            letterSpacing: '0.02em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          Done
        </button>
      </div>
    </div>
  );
}
